import React, { useRef, useState, useEffect } from "react";
import { Play, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

/**
 * VideoCard
 * - Shows a clickable thumbnail (first frame via preload="metadata") with a play button overlay.
 * - On click, opens a Dialog with a full-size video player (autoplay, controls).
 * Props:
 *   src         direct mp4 url
 *   title       small title shown over the thumb
 *   subtitle    small subtitle under title
 *   aspect      tailwind aspect class (default aspect-video)
 *   accent      "light" | "dark"  (controls overlay colors)
 *   poster      optional poster image url
 *   testid
 */
export default function VideoCard({
  src,
  title,
  subtitle,
  aspect = "aspect-video",
  accent = "light",
  poster,
  testid = "video-card",
}) {
  const [open, setOpen] = useState(false);
  const thumbRef = useRef(null);
  const dialogVideoRef = useRef(null);

  // Pause dialog video on close
  useEffect(() => {
    if (!open && dialogVideoRef.current) {
      try {
        dialogVideoRef.current.pause();
      } catch (error) {
        console.warn("Could not pause video:", error);
      }
    }
  }, [open]);

  const isDark = accent === "dark";

  return (
    <>
      <button
        type="button"
        data-testid={testid}
        onClick={() => setOpen(true)}
        className={`group relative block w-full ${aspect} overflow-hidden border ${
          isDark ? "border-white/15 bg-white/5" : "border-[#E3DDE8] bg-[#F0EDF4]"
        } cursor-pointer text-left`}
        aria-label={`Reproducir vídeo: ${title || "Core"}`}
      >
        <video
          ref={thumbRef}
          src={src}
          poster={poster}
          preload="metadata"
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className={`absolute inset-0 ${isDark ? "bg-black/35" : "bg-[#2E2638]/30"} group-hover:${isDark ? "bg-black/15" : "bg-[#2E2638]/15"} transition-colors`} />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#8B1538] group-hover:bg-[#6A0F2A] flex items-center justify-center shadow-[0_8px_30px_rgba(139,21,56,0.5)] transition-all duration-300 group-hover:scale-110">
            <Play size={28} className="text-white ml-1.5" fill="white" />
          </div>
        </div>
        {(title || subtitle) && (
          <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
            {title && <div className="font-display font-bold text-white text-lg sm:text-xl leading-snug">{title}</div>}
            {subtitle && <div className="text-white/80 text-xs sm:text-sm mt-1">{subtitle}</div>}
          </div>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl p-0 bg-black border-0 overflow-hidden">
          <button
            onClick={() => setOpen(false)}
            className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur transition-colors"
            aria-label="Cerrar"
            data-testid={`${testid}-close`}
          >
            <X size={18} />
          </button>
          <video
            ref={dialogVideoRef}
            src={src}
            controls
            autoPlay
            playsInline
            className="w-full h-auto max-h-[85vh] bg-black"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
